/*
 * SPDX-FileCopyrightText: © 2025 Schneider Electric
 *
 * SPDX-License-Identifier: LGPL-2.1-only
 */
@import url("../shared.css");

:host {
  display: block;
  width: 100%;
  container-type: inline-size;

  --qds-main-subsection-title: bold 1.375rem/1.5rem var(--qds-font-family-brand);
}

.content-area {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin: auto;
}

.title-description {
  display: flex;
  flex-direction: column;
}

.heading {
  width: 100%;
}

.extension {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.description {
  margin: 0;
  padding: 0;
  color: var(--qds-theme-text-emphasized);
}

::slotted([slot="cta"]) {
  display: flex;
  align-items: center;
}

[hidden] {
  display: none;
}

[data-layout="standard"] {
  .content-area {
    max-width: 1424px;
  }

  .heading {
    display: flex;
    justify-content: space-between;
  }
}

[data-layout="centered"] {
  .content-area {
    max-width: 800px;
  }

  .heading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .title-description {
      text-align: center;
    }
  }
}

[data-layer="hero"] {
  .content-area {
    gap: var(--qds-main-section-hero-gap-children-unrelated);
  }

  ::slotted([slot="cta"]) {
    gap: var(--qds-control-jumbo-gap-siblings-related);
  }

  .extension {
    gap: var(--qds-main-section-hero-gap-children-related);
  }

  .heading {
    gap: var(--qds-main-section-hero-gap-children-unrelated);

    .title-description {
      gap: var(--qds-main-section-hero-gap-children-related);
    }
  }

  .description {
    font: var(--qds-main-section-hero-text);
  }
}

[data-layer="section"] {
  .content-area {
    gap: var(--qds-main-section-heading-gap-children-unrelated);
  }

  ::slotted([slot="cta"]) {
    gap: var(--qds-control-standard-gap-siblings-related);
  }

  .extension {
    gap: var(--qds-main-section-gap-children-related);
  }

  .heading {
    gap: var(--qds-main-section-heading-gap-children-unrelated);

    .title-description {
      gap: var(--qds-main-section-gap-children-related);
    }
  }

  .description {
    font: var(--qds-main-section-hero-text);
  }
}

[data-layer="subsection"] {
  .qds-padding-top {
    padding-top: var(--qds-main-subsection-heading-padding-horizontal);
  }

  .qds-padding-bottom {
    padding-bottom: var(--qds-main-subsection-heading-padding-horizontal);
  }

  .qds-padding-inline {
    padding-inline: var(--qds-main-subsection-heading-padding-vertical);
  }

  .content-area {
    gap: var(--qds-main-subsection-gap-children-unrelated);
  }

  ::slotted([slot="cta"]) {
    gap: var(--qds-control-small-gap-siblings-related);
  }

  .extension {
    gap: var(--qds-main-subsection-heading-gap-children-related);
  }

  .heading {
    gap: var(--qds-main-subsection-gap-children-unrelated);

    .title-description {
      gap: var(--qds-main-subsection-heading-gap-children-related);
    }
  }

  &section {
    border-radius: var(--qds-main-subsection-heading-border-radius);
  }

  .description {
    font: var(--qds-main-section-hero-text);
  }
}

[data-importance="standard"] {
  background-color: var(--qds-theme-main-background);
}

[data-importance="emphasized"] {
  background-color: var(--qds-theme-main-subsection-standard-background);
}

[data-importance="subdued"] {
  background-color: transparent;
}

/* Desktop (1024 - 1279px) and Large Desktop (+1280px); */

@container (width > 1023px) {
  [data-layer="hero"] {
    .qds-padding-top {
      padding-top: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-block-heading-padding-horizontal);
    }
  }

  [data-layer="section"] {
    .qds-padding-top {
      padding-top: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-block-heading-padding-horizontal);
    }
  }

  [data-layout="centered"] {
    .extension {
      align-items: center;
      justify-content: center;
    }
  }
}

/*  Tablet(768 - 1023px); */

@container (width > 767px) and (width <= 1023px) {
  [data-layer="hero"] {
    .qds-padding-top {
      padding-top: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-block-heading-padding-horizontal);
    }
  }

  [data-layer="section"] {
    .qds-padding-top {
      padding-top: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-block-heading-padding-horizontal);
    }
  }

  .heading {
    flex-direction: column;
  }

  [data-layout="centered"] {
    .extension {
      align-items: center;
      justify-content: center;
    }
  }
}

/*  Mobile(320 - 767px); */

@container (width <= 767px) {
  [data-layer="hero"] {
    .qds-padding-top {
      padding-top: var(--qds-main-section-hero-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-main-section-hero-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-main-section-hero-padding-horizontal);
    }
  }

  [data-layer="section"] {
    .qds-padding-top {
      padding-top: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-bottom {
      padding-bottom: var(--qds-block-heading-padding-vertical);
    }

    .qds-padding-inline {
      padding-inline: var(--qds-block-heading-padding-horizontal);
    }
  }

  .heading {
    flex-direction: column;
  }

  .extension {
    align-items: stretch;
  }

  ::slotted([slot="cta"]) {
    flex-flow: column;
    align-items: stretch;
    width: 100%;
  }
}
